import React, { useState, useEffect, useCallback } from 'react';
import { LoadingSpinner, EmptyState } from '../../../components/layout/SharedComponents';
import api from '../../../services/api';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Search, Download, ChevronLeft, ChevronRight, ArrowUp, ArrowDown, FileSpreadsheet, Info } from 'lucide-react';
import { formatTimestamp as fmtTs } from '../../Requisitions/constants';
import { useAuth } from '../../../contexts/AuthContext';
import { toast } from 'sonner';

const ACTION_COLORS = {
  CREATE: 'bg-green-100 text-green-700', UPDATE: 'bg-blue-100 text-blue-700',
  DELETE: 'bg-red-100 text-red-700', APPROVE: 'bg-emerald-100 text-emerald-700',
  REJECT: 'bg-red-100 text-red-700', SUBMIT: 'bg-amber-100 text-amber-700',
  CANCEL: 'bg-gray-100 text-gray-700', ISSUE: 'bg-purple-100 text-purple-700',
  LOGIN: 'bg-sky-100 text-sky-700', LOGOUT: 'bg-gray-100 text-gray-600',
  PUBLISH: 'bg-indigo-100 text-indigo-700', STATE_CHANGE: 'bg-violet-100 text-violet-700',
  AWARD: 'bg-emerald-100 text-emerald-800', INVITE_SENT: 'bg-blue-100 text-blue-700',
  QA_ANSWER: 'bg-amber-100 text-amber-700', USER_REG_REJECTED: 'bg-red-100 text-red-700',
};

export function AuditLogsPanel() {
  const { systemSettings } = useAuth();
  const tz = systemSettings?.timezone || 'America/Los_Angeles';
  const fmtTz = (d) => fmtTs(d, tz);

  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [moduleFilter, setModuleFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [modules, setModules] = useState([]);
  const [actions, setActions] = useState([]);
  const [sortKey, setSortKey] = useState('timestamp');
  const [sortDir, setSortDir] = useState('desc');
  const pageSize = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: page.toString(), page_size: pageSize.toString() });
      if (search) params.set('search', search);
      if (moduleFilter) params.set('module', moduleFilter);
      if (actionFilter) params.set('action', actionFilter);
      if (sortKey) params.set('sort', sortKey);
      if (sortDir) params.set('sort_dir', sortDir);
      const r = await api.get(`/api/system/audit-trail?${params}`);
      setLogs(r.data.data || []);
      setTotal(r.data.total || 0);
      if (r.data.modules) setModules(r.data.modules);
      if (r.data.actions) setActions(r.data.actions);
    } catch {}
    setLoading(false);
  }, [page, search, moduleFilter, actionFilter, sortKey, sortDir]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.ceil(total / pageSize);

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
    setPage(1);
  };

  const SortHeader = ({ label, field, className = '' }) => (
    <th className={`text-left px-3 py-2.5 font-semibold cursor-pointer select-none hover:bg-muted/80 transition-colors ${className}`}
      onClick={() => handleSort(field)}>
      <span className="flex items-center gap-1">
        {label}
        {sortKey === field && (sortDir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
      </span>
    </th>
  );

  // Excel export
  const exportExcel = async () => {
    toast.info('Generating Excel export...');
    try {
      // Fetch all matching records (up to 5000)
      const params = new URLSearchParams({ page: '1', page_size: '5000' });
      if (search) params.set('search', search);
      if (moduleFilter) params.set('module', moduleFilter);
      if (actionFilter) params.set('action', actionFilter);
      if (sortKey) params.set('sort', sortKey);
      if (sortDir) params.set('sort_dir', sortDir);
      const r = await api.get(`/api/system/audit-trail?${params}`);
      const allLogs = r.data.data || [];

      const XLSX = await import('xlsx');
      const wb = XLSX.utils.book_new();

      const headers = ['Date & Time (' + tz + ')', 'User', 'Email', 'Action', 'Module', 'Entity', 'Details', 'IP Address'];
      const rows = allLogs.map(l => [
        fmtTz(l.timestamp),
        l.actor_name || l.actor_id || '',
        l.actor_email || '',
        l.action || '',
        l.module || '',
        l.entity_id || '',
        l.notes || '',
        l.ip_address || '',
      ]);

      const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);

      // Column widths
      ws['!cols'] = [{ wch: 22 }, { wch: 20 }, { wch: 28 }, { wch: 14 }, { wch: 16 }, { wch: 16 }, { wch: 50 }, { wch: 16 }];

      XLSX.utils.book_append_sheet(wb, ws, 'Audit Trail');
      XLSX.writeFile(wb, `Audit_Trail_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success(`Exported ${allLogs.length} entries to Excel`);
    } catch (e) { toast.error('Export failed: ' + e.message); }
  };

  return (
    <div data-testid="audit-logs-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold mb-1">System Audit Trail</h1>
          <p className="text-sm text-muted-foreground">{total.toLocaleString()} entries — every record change is logged</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 text-xs text-muted-foreground border rounded-lg px-2 py-1">
            <Info className="w-3 h-3" />
            <span>All times shown in <strong>{tz.replace(/_/g, ' ')}</strong></span>
          </div>
          <Button size="sm" variant="outline" onClick={exportExcel} data-testid="audit-export-excel-btn">
            <FileSpreadsheet className="h-3.5 w-3.5 mr-1" /> Export Excel
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 mb-4">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-2 h-4 w-4 text-muted-foreground" />
          <Input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search user, entity, notes..." className="pl-8 h-8 text-sm" data-testid="audit-search" />
        </div>
        <select value={moduleFilter} onChange={e => { setModuleFilter(e.target.value); setPage(1); }}
          className="h-8 rounded-lg border border-input bg-background px-2 text-xs" data-testid="audit-module-filter">
          <option value="">All Modules</option>
          {modules.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
        <select value={actionFilter} onChange={e => { setActionFilter(e.target.value); setPage(1); }}
          className="h-8 rounded-lg border border-input bg-background px-2 text-xs" data-testid="audit-action-filter">
          <option value="">All Actions</option>
          {actions.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        {(search || moduleFilter || actionFilter) && (
          <Button size="sm" variant="ghost" onClick={() => { setSearch(''); setModuleFilter(''); setActionFilter(''); setPage(1); }}>Clear</Button>
        )}
      </div>

      {/* Table */}
      {loading ? <LoadingSpinner /> : logs.length === 0 ? (
        <EmptyState title="No audit entries" description="No records match your search criteria." />
      ) : (
        <div className="border rounded-xl overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-muted/50 border-b">
                <SortHeader label={`Date & Time (${tz.replace(/_/g, ' ')})`} field="timestamp" className="w-[170px]" />
                <SortHeader label="User" field="actor_name" />
                <SortHeader label="Action" field="action" className="w-[90px]" />
                <SortHeader label="Module" field="module" />
                <SortHeader label="Entity" field="entity_id" />
                <SortHeader label="Details" field="notes" />
                <SortHeader label="IP Address" field="ip_address" className="w-[120px]" />
              </tr>
            </thead>
            <tbody>
              {logs.map((l, i) => (
                <tr key={i} className="border-b hover:bg-accent/20">
                  <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{fmtTz(l.timestamp)}</td>
                  <td className="px-3 py-2">
                    <div>{l.actor_name || l.actor_id}</div>
                    {l.actor_email && <div className="text-[10px] text-muted-foreground">{l.actor_email}</div>}
                  </td>
                  <td className="px-3 py-2">
                    <Badge className={`text-[9px] ${ACTION_COLORS[l.action] || 'bg-gray-100 text-gray-700'}`}>{l.action}</Badge>
                  </td>
                  <td className="px-3 py-2">{l.module}</td>
                  <td className="px-3 py-2 font-semibold">{l.entity_id || '—'}</td>
                  <td className="px-3 py-2 max-w-[250px] truncate text-muted-foreground" title={l.notes || ''}>{l.notes || '—'}</td>
                  <td className="px-3 py-2 text-muted-foreground font-mono text-[10px]">{l.ip_address || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-xs text-muted-foreground">
            Showing {((page - 1) * pageSize) + 1}–{Math.min(page * pageSize, total)} of {total}
          </p>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
              <ChevronLeft className="h-3.5 w-3.5" />
            </Button>
            <span className="text-xs px-2">Page {page} of {totalPages}</span>
            <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
